import React, { useEffect, useState } from 'react';

type RelayState =
  | { status: 'loading'; message: string }
  | { status: 'success'; message: string }
  | { status: 'error'; message: string };

function isLoopbackCallback(value: string | null): string | null {
  if (!value) return null;

  try {
    const parsed = new URL(value);
    const host = parsed.hostname.trim().toLowerCase();
    if (
      parsed.protocol !== 'http:' ||
      !['127.0.0.1', 'localhost', '::1', '[::1]'].includes(host)
    ) {
      return null;
    }
    return parsed.toString();
  } catch {
    return null;
  }
}

async function postRelayResult(callbackUrl: string, payload: unknown) {
  const response = await fetch(callbackUrl, {
    method: 'POST',
    credentials: 'omit',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Local relay callback failed with ${response.status}`);
  }
}

function formatDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function normalizeHandlingStatus(value: unknown) {
  return String(value || '').trim().toLowerCase();
}

function isHandledStatus(value: unknown) {
  return ['done', 'ignored', 'resolved', 'closed', 'verified'].includes(normalizeHandlingStatus(value));
}

async function fetchPendingDates(endDate: string, days: number) {
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(end.getTime())) {
    throw new Error('Missing or invalid endDate for pending-date lookup.');
  }

  const results: Array<{
    date: string;
    total: number;
    bugs: number;
    suggestions: number;
    open: number;
    source: 'remote';
  }> = [];

  for (let index = 0; index < days; index += 1) {
    const current = new Date(end);
    current.setDate(end.getDate() - index);
    const date = formatDateKey(current);
    const response = await fetch(`/api/v1/feedback-reports/daily?date=${encodeURIComponent(date)}`, {
      credentials: 'include',
      headers: {
        accept: 'application/json',
      },
    });

    if (!response.ok) {
      continue;
    }

    const payload = await response.json().catch(() => null);
    const data = payload?.success === true && payload?.data ? payload.data : payload;
    if (!data || Number(data?.counts?.total || 0) <= 0) {
      continue;
    }

    const bugs = Array.isArray(data.bugs) ? data.bugs : [];
    const suggestions = Array.isArray(data.suggestions) ? data.suggestions : [];
    const actionable = [...bugs, ...suggestions];
    const open = actionable.filter((item) => !isHandledStatus(item?.handlingStatus)).length;
    if (open <= 0) {
      continue;
    }

    results.push({
      date: String(data.localDate || date),
      total: Number(data?.counts?.total || actionable.length || 0),
      bugs: Number(data?.counts?.bugs || bugs.length || 0),
      suggestions: Number(data?.counts?.suggestions || suggestions.length || 0),
      open,
      source: 'remote',
    });
  }

  return results;
}

export default function FeedbackExportRelayPage() {
  const [relayState, setRelayState] = useState<RelayState>({
    status: 'loading',
    message: 'Preparing secure feedback export relay...',
  });

  useEffect(() => {
    const url = new URL(window.location.href);
    const mode = String(url.searchParams.get('mode') || 'export').trim().toLowerCase();
    const date = String(url.searchParams.get('date') || '').trim();
    const callbackUrl = isLoopbackCallback(url.searchParams.get('callbackUrl'));
    const includeScreenshots = ['1', 'true', 'yes', 'on'].includes(
      String(url.searchParams.get('includeScreenshots') || '').trim().toLowerCase()
    );
    const endDate = String(url.searchParams.get('endDate') || '').trim() || formatDateKey(new Date());
    const lookbackDays = Math.max(
      1,
      Math.min(120, Number.parseInt(String(url.searchParams.get('days') || '30'), 10) || 30)
    );
    const shouldAutoClose = ['1', 'true', 'yes', 'on'].includes(
      String(url.searchParams.get('autoclose') || '').trim().toLowerCase()
    );

    if (mode === 'export' && !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      setRelayState({
        status: 'error',
        message: 'Missing or invalid date. Open this helper from the local Codex export tool.',
      });
      return;
    }

    if (!callbackUrl) {
      setRelayState({
        status: 'error',
        message: 'Missing or invalid callback URL. Only localhost callbacks are allowed.',
      });
      return;
    }

    let isActive = true;

    const run = async () => {
      try {
        if (mode === 'pending-dates') {
          setRelayState({
            status: 'loading',
            message: `Scanning the last ${lookbackDays} day(s) of feedback reports from your signed-in PMapp session...`,
          });

          const payload = {
            dates: await fetchPendingDates(endDate, lookbackDays),
          };

          await postRelayResult(callbackUrl, {
            success: true,
            payload,
          });

          if (!isActive) return;
          setRelayState({
            status: 'success',
            message: `Pending feedback dates have been sent to your local Codex helper. You can close this tab.`,
          });
        } else {
          setRelayState({
            status: 'loading',
            message: `Fetching feedback bundle for ${date} from your signed-in PMapp session...`,
          });

          const response = await fetch(
            `/api/v1/feedback-reports/export?date=${encodeURIComponent(date)}${includeScreenshots ? '&includeScreenshots=1' : ''}`,
            {
              credentials: 'include',
              headers: {
                accept: 'application/json',
              },
            }
          );

          const payload = await response.json().catch(() => null);
          if (!response.ok) {
            const errorMessage = String(
              payload?.error?.message || `Feedback export failed with ${response.status}`
            );
            throw new Error(errorMessage);
          }

          await postRelayResult(callbackUrl, {
            success: true,
            payload,
          });

          if (!isActive) return;
          setRelayState({
            status: 'success',
            message: `Feedback bundle for ${date} has been sent to your local Codex helper. You can close this tab.`,
          });
        }

        if (shouldAutoClose) {
          window.setTimeout(() => {
            window.close();
          }, 750);
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        try {
          await postRelayResult(callbackUrl, {
            success: false,
            error: {
              message,
            },
          });
        } catch {
          // If the local listener is unavailable we still show the browser-side error.
        }

        if (!isActive) return;
        setRelayState({
          status: 'error',
          message,
        });
      }
    };

    void run();

    return () => {
      isActive = false;
    };
  }, []);

  const isError = relayState.status === 'error';
  const isSuccess = relayState.status === 'success';

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f6f7fb 0%, #eef3f9 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div
        style={{
          width: 'min(560px, 100%)',
          background: '#ffffff',
          border: '1px solid #d7dee7',
          borderRadius: '18px',
          boxShadow: '0 22px 48px rgba(23, 59, 132, 0.10)',
          padding: '28px',
        }}
      >
        <div
          style={{
            fontSize: '12px',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: '#5f6b7a',
            marginBottom: '12px',
          }}
        >
          PMapp Feedback Relay
        </div>
        <h1
          style={{
            margin: '0 0 12px',
            fontSize: '24px',
            lineHeight: 1.2,
            color: '#173b84',
          }}
        >
          {isSuccess ? 'Bundle Ready' : isError ? 'Relay Failed' : 'Preparing Export'}
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: '15px',
            lineHeight: 1.6,
            color: '#273142',
          }}
        >
          {relayState.message}
        </p>
        {!isSuccess && (
          <p
            style={{
              margin: '18px 0 0',
              fontSize: '13px',
              lineHeight: 1.6,
              color: '#5f6b7a',
            }}
          >
            Keep this tab open for a moment while PMapp sends the protected export back to your local Codex workflow.
          </p>
        )}
      </div>
    </div>
  );
}
